import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import axios from '../../../services/api';
import { toast } from 'react-toastify';
import { FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import { generateUUID } from '../../../services/utils';
import MapModal from './MapModal';
import { useTranslation } from 'react-i18next';

export default function CreateCommunityModal({ isOpen, onClose, onSuccess, building = null, community = null }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    buildingId: generateUUID(),
    buildingName: '',
    buildingAddress: community?.communityAddress,
    longitude: community?.longitude,
    latitude: community?.latitude,
    serviceRange: '',
    contacts: '',
    contactsPhone: '',
    email: '',
    communityId: community ? community._id : ''
  });
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  const handleLocationSelect = ({ latitude, longitude, name }) => {
    setFormData(prev => ({
      ...prev,
      latitude: latitude.toFixed(6),
      longitude: longitude.toFixed(6),
      buildingAddress: name
    }));
  };

  useEffect(() => {
    if (building) {
      setFormData({
        buildingId: building.buildingId || '',
        buildingName: building.buildingName || '',
        buildingAddress: building.buildingAddress || '',
        longitude: building.longitude || '',
        latitude: building.latitude || '',
        serviceRange: building.serviceRange || '',
        contacts: building.contacts || '',
        contactsPhone: building.contactsPhone || '',
        email: building.email || '',
        communityId: community._id
      });
    } else {
      setFormData({
        buildingId: generateUUID(),
        buildingName: '',
        buildingAddress: community?.communityAddress,
        longitude: community?.longitude,
        latitude: community?.latitude,
        serviceRange: '',
        contacts: '',
        contactsPhone: '',
        email: '',
        communityId: community._id
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [building]);
    // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!formData.buildingName || !formData.buildingAddress || !formData.longitude || !formData.latitude) {
      toast.error(t('Please fill in all required fields'));
      return;
    }

    try {
      if (building) {
        await axios.put(`/buildings/${building._id}`, formData);
      } else {
        await axios.post('/buildings', formData);
      }
      setFormData({
        buildingId: generateUUID(),
        buildingName: '',
        buildingAddress: '',
        longitude: '',
        latitude: '',
        serviceRange: '',
        contacts: '',
        contactsPhone: '',
        email: '',
        communityId: community._id
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
    }

  };
  if (!isOpen) return null;

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => { }}>
          <div className="fixed inset-0 bg-black/50 flex items-center justify-end pr-[10%] pt-8 z-50 app-text">
            <div className="bg-white w-[calc(85vh*1)] h-[70vh] rounded-lg shadow-xl">
              {/* Black header */}
              <div className="bg-black text-white px-4 py-3 rounded-t-lg flex justify-between items-center">
                <h2 className="text-lg font-semibold">
                  {building ? t('Edit Building') : t('Create New Building')}
                </h2>
                <button onClick={onClose} className="text-white/70 hover:text-white transition-colors">
                  <FaTimes />
                </button>
              </div>

              {/* White content area */}
              <div className="p-4 h-[calc(70vh-116px)] overflow-y-auto">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('Building ID')}</label>
                    <input
                      type="text"
                      value={formData.buildingId}
                      onChange={(e) => setFormData({ ...formData, buildingId: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      disabled
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('Building Name')}*</label>
                    <input
                      type="text"
                      value={formData.buildingName}
                      onChange={(e) => setFormData({ ...formData, buildingName: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('Building Address')}*</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData.buildingAddress}
                        onChange={(e) => setFormData({ ...formData, buildingAddress: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                      <button
                        onClick={() => setIsMapModalOpen(true)}
                        className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        title="Find on map"
                      >
                        <FaMapMarkerAlt className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('longitude')}*</label>
                    <input
                      type="text"
                      value={formData.longitude}
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('latitude')}*</label>
                    <input
                      type="text"
                      value={formData.latitude}
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('Service Range (Meters)')}</label>
                    <input
                      type="number"
                      value={formData.serviceRange}
                      onChange={(e) => setFormData({ ...formData, serviceRange: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">{t('Contacts')}</label>
                      <input
                        type="text"
                        value={formData.contacts}
                        onChange={(e) => setFormData({ ...formData, contacts: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">{t('Contacts Phone')}</label>
                      <input
                        type="text"
                        value={formData.contactsPhone}
                        onChange={(e) => setFormData({ ...formData, contactsPhone: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium mb-1">{t('Email')}</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer with buttons */}
              <div className="px-4 py-3 border-t border-gray-200">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="px-4 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    {building ? t('Update') : t('Save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      <MapModal
        isOpen={isMapModalOpen}
        onClose={() => setIsMapModalOpen(false)}
        address2={formData.buildingAddress}
        onSelectLocation={handleLocationSelect}
      />
    </>
  );
}
